import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LoadingState } from "@/components/States";

export default function AdminTodayResult() {
  const navigate = useNavigate();
  const [date, setDate] = useState("");
  const [fr, setFr] = useState("");
  const [sr, setSr] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/results/${d}`);
      setFr(data.fr || "");
      setSr(data.sr || "");
    } catch (e) {
      toast.error("Failed to load result");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/date/today");
      setDate(data.date);
      await load(data.date);
    })();
  }, [load]);

  const changeDate = (d) => { setDate(d); if (d) load(d); };

  const submit = async () => {
    if (!fr.trim() || !sr.trim()) {
      toast.error("Please enter both F/R and S/R.");
      return;
    }
    setSaving(true);
    try {
      // Save and publish so it appears live on the homepage immediately
      await api.post("/admin/results", { date, fr: fr.trim(), sr: sr.trim(), published: true });
      toast.success("Result saved and published — live on homepage.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-today-result">
      {loading ? (
        <LoadingState />
      ) : (
        <div className="rounded-2xl border-2 border-dashed border-emerald-300 bg-white p-5 sm:p-7">
          {/* Date */}
          <label className="block text-lg font-bold text-slate-500 mb-2">Date:</label>
          <input
            type="date"
            value={date}
            onChange={(e) => changeDate(e.target.value)}
            data-testid="today-date-input"
            className="w-full sm:w-64 h-14 px-4 rounded-lg border border-slate-300 text-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/40"
          />

          {/* F/R */}
          <label className="block text-lg font-bold text-slate-500 mt-7 mb-2">F/R (10:30 AM) :</label>
          <input
            type="text"
            value={fr}
            onChange={(e) => setFr(e.target.value)}
            data-testid="today-fr-input"
            className="w-full h-14 px-4 rounded-lg border border-slate-300 text-2xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/40"
            placeholder="Enter number or text"
          />

          {/* Buttons */}
          <div className="flex justify-end gap-3 my-4">
            <button onClick={submit} disabled={saving} data-testid="today-save-btn" className="h-12 px-8 rounded-lg bg-[#4a76a8] text-white font-bold text-lg hover:bg-[#3d6493] transition-colors disabled:opacity-60">
              {saving ? "Saving…" : "Submit"}
            </button>
            <button onClick={() => navigate("/admin/previous")} data-testid="today-back-btn" className="h-12 px-8 rounded-lg bg-[#4a76a8] text-white font-bold text-lg hover:bg-[#3d6493] transition-colors">
              Back
            </button>
          </div>

          {/* S/R */}
          <label className="block text-lg font-bold text-slate-500 mt-2 mb-2">S/R (11:30 AM):</label>
          <input
            type="text"
            value={sr}
            onChange={(e) => setSr(e.target.value)}
            data-testid="today-sr-input"
            className="w-full h-14 px-4 rounded-lg border border-slate-300 text-2xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/40"
            placeholder="Enter number or text"
          />
        </div>
      )}
    </div>
  );
}
